/**
 * NotificationsPage — pagina dedicata con storico completo e filtri.
 *
 * Mostra tutte le notifiche dell'utente, con tre filtri rapidi
 * ("Tutte", "Non lette", "Per tipo") e azione di mark-all-read.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCheck, CalendarDays, FileWarning, Wrench, StickyNote, Inbox, ShoppingCart,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { notificationsApi } from '../lib/api';

const TYPE_META = {
  leave_request: { label: 'Ferie & Permessi', icon: CalendarDays, color: 'text-amber-600 bg-amber-50' },
  leave_decided: { label: 'Esito ferie/permessi', icon: CalendarDays, color: 'text-emerald-600 bg-emerald-50' },
  document_expiring: { label: 'Documenti', icon: FileWarning, color: 'text-red-600 bg-red-50' },
  ticket_opened: { label: 'Assistenza', icon: Wrench, color: 'text-blue-600 bg-blue-50' },
  store_note: { label: 'Note negozio', icon: StickyNote, color: 'text-emerald-600 bg-emerald-50' },
  order_created: { label: 'Ordini', icon: ShoppingCart, color: 'text-indigo-600 bg-indigo-50' },
  order_decided: { label: 'Esito ordine', icon: ShoppingCart, color: 'text-emerald-600 bg-emerald-50' },
  order_delivered: { label: 'Ordine consegnato', icon: ShoppingCart, color: 'text-blue-600 bg-blue-50' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return 'ora';
  if (m < 60) return `${m}m fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}g fa`;
  return new Date(iso).toLocaleString('it-IT');
}

function FilterChip({ value, label, count, active, onSelect }) {
  return (
    <button
      onClick={() => onSelect(value)}
      className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
        active
          ? 'bg-slate-900 text-white'
          : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
      }`}
      data-testid={`notifications-filter-${value}`}
    >
      {label}
      {typeof count === 'number' && count > 0 && (
        <span className={`text-[10px] px-1.5 py-0.5 ${
          active ? 'bg-white text-slate-900' : 'bg-slate-100 text-slate-600'
        }`}>{count}</span>
      )}
    </button>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | <type>
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filter === 'unread') params.unread_only = true;
      else if (filter !== 'all') params.type = filter;
      const { data } = await notificationsApi.list(params);
      setItems(data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const unreadCount = useMemo(
    () => items.filter((n) => !n.is_read).length,
    [items],
  );

  const handleClick = async (n) => {
    if (!n.is_read) {
      try {
        await notificationsApi.markRead(n.notification_id);
        setItems((prev) => prev.map((x) =>
          x.notification_id === n.notification_id ? { ...x, is_read: true } : x,
        ));
      } catch (e) { /* noop */ }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (e) { /* noop */ }
  };

  const setFilterCb = setFilter;

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="notifications-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Activity feed</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Bell className="w-7 h-7 text-slate-700" /> Notifiche
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Tutte le cose a cui badare: richieste ferie in attesa, documenti in scadenza, ticket aperti, note nuove sui tuoi negozi.
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={handleMarkAllRead}
            data-testid="notifications-mark-all-read-page"
          >
            <CheckCheck className="w-4 h-4" /> Segna tutte come lette
          </Button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        <FilterChip value="all" label="Tutte" active={filter === 'all'} onSelect={setFilterCb} />
        <FilterChip value="unread" label="Non lette" count={unreadCount} active={filter === 'unread'} onSelect={setFilterCb} />
        <FilterChip value="leave_request" label="Ferie" active={filter === 'leave_request'} onSelect={setFilterCb} />
        <FilterChip value="document_expiring" label="Documenti" active={filter === 'document_expiring'} onSelect={setFilterCb} />
        <FilterChip value="ticket_opened" label="Assistenza" active={filter === 'ticket_opened'} onSelect={setFilterCb} />
        <FilterChip value="store_note" label="Note" active={filter === 'store_note'} onSelect={setFilterCb} />
      </div>

      {loading && (
        <div className="text-center text-slate-400 py-12 text-sm">Caricamento…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-20" data-testid="notifications-page-empty">
          <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Nessuna notifica.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white border border-slate-200">
          {items.map((n) => {
            const meta = TYPE_META[n.type] || { label: 'Notifica', icon: Bell, color: 'text-slate-600 bg-slate-100' };
            const Icon = meta.icon;
            return (
              <button
                key={n.notification_id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-4 md:px-5 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors flex gap-4 ${
                  !n.is_read ? 'bg-blue-50/40' : ''
                }`}
                data-testid={`notification-row-${n.notification_id}`}
              >
                <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <p className={`text-sm leading-snug ${!n.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                      {n.title}
                    </p>
                    {!n.is_read && (
                      <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" aria-label="Non letta" />
                    )}
                  </div>
                  {n.body && (
                    <p className="text-sm text-slate-500 mt-1">{n.body}</p>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1.5 uppercase tracking-wider">
                    {meta.label} · {timeAgo(n.created_at)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
